import { EventInput } from "@fullcalendar/core";
import { Button, Dialog, Select, TextArea, TextField } from "@resource/atlas";
import { useFlags } from "@resource/client-ffs";
import { AvailabilityCalendar } from "client/components/input/availability-calendar";
import InputGroup from "components/Input/InputGroup";
import FeatureFlags from "generated/FeatureFlags";
import { RefObject, useCallback, useRef, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import { Controller, useForm } from "react-hook-form";
import mergeRefs from "react-merge-refs";
import { PersistableCalendarEvent } from "shared/guide-content/rich-blocks/calendar-utils/types";
import {
  labelForDuration,
  MeetingDuration,
} from "shared/guide-content/rich-blocks/request-availability";

import { useRequestAvailability } from "./context";
import { OverlayCalendars } from "./OverlayCalendars";
import {
  getLatestSelectionOrSuggestionDate,
  getPlaceholderTitle,
} from "./utils";

const meetingDurations: MeetingDuration[] = [
  0, 15, 30, 45, 60, 90, 120, 180, 240, 480,
];

type RequestAvailabilityModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  onConfirm?: (data: RequestAvailabilityFormData) => void;
  data: RequestAvailabilityFormData;
  selections?: PersistableCalendarEvent[];
  loading?: boolean;
  initialFocusRef?: RefObject<HTMLElement>;
};

type RequestAvailabilityModalViewProps = Omit<
  RequestAvailabilityModalProps,
  "isOpen"
>;

type RequestAvailabilityFormData = {
  suggestions: PersistableCalendarEvent[];
  meetingTitle: string;
  meetingDescription: string;
  meetingDuration: number;
  timezone: string;
};

function RequestAvailabilityModalView({
  onClose,
  onConfirm,
  data,
  selections = [],
  loading,
  initialFocusRef,
}: RequestAvailabilityModalViewProps) {
  const { organization } = useRequestAvailability();

  const [overlayEvents, setOverlayEvents] = useState<EventInput[]>([]);
  const { [FeatureFlags.ENABLE_OVERLAY_CALENDARS]: enableOverlayCalendars } =
    useFlags();
  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    setValue,
  } = useForm<RequestAvailabilityFormData>({
    defaultValues: {
      suggestions: [],
      meetingTitle: data.meetingTitle || getPlaceholderTitle(),
      meetingDescription: data.meetingDescription,
      meetingDuration: data.meetingDuration,
      timezone: data.timezone,
    },
  });

  const selectedTimezone = watch("timezone");

  const handleContinue = useCallback(
    (data: RequestAvailabilityFormData) => {
      onConfirm?.({
        ...data,
        meetingTitle: data.meetingTitle || getPlaceholderTitle(),
      });
    },
    [onConfirm]
  );

  const initialDate = getLatestSelectionOrSuggestionDate({
    selections,
  });

  return (
    <Dialog.View
      header={{ title: "Request availability" }}
      footer={{
        leftActions: (
          <Button
            onClick={() => {
              onClose?.();
            }}
          >
            Cancel
          </Button>
        ),
        rightActions: (
          <Button
            variant="primary"
            onClick={handleSubmit(handleContinue)}
            disabled={loading}
            isLoading={loading}
          >
            Confirm
          </Button>
        ),
      }}
    >
      <div className="w-full h-full flex gap-6">
        <div className="w-64 shrink-0 space-y-4">
          <InputGroup
            label="Meeting title"
            error={errors.meetingTitle}
            inputField={
              <Controller
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder={`ex: Meeting for ${organization?.name}`}
                    inputRef={mergeRefs([field.ref, initialFocusRef ?? null])}
                  />
                )}
                name="meetingTitle"
                control={control}
              />
            }
          />
          <Controller
            name="meetingDescription"
            control={control}
            render={({ field }) => (
              <TextArea
                label="Description"
                placeholder="ex: Let's find a time to meet."
                {...field}
                inputRef={field.ref}
              />
            )}
          />
          <div className="space-y-2">
            <p className="text-body-md-heavy">Meeting length</p>
            <Controller
              name="meetingDuration"
              control={control}
              render={({ field }) => (
                <Select.Root
                  value={field.value?.toString()}
                  setValue={(val) => {
                    setValue("meetingDuration", parseInt(val as string, 10));
                  }}
                >
                  <Select.Trigger className="w-full bg-light-gray-500" />
                  <Select.Content
                    defaultValue={field.value}
                    placeholder="Select"
                  >
                    {meetingDurations.map((value) => (
                      <Select.Option key={value} value={value.toString()}>
                        {labelForDuration(value)}
                      </Select.Option>
                    ))}
                  </Select.Content>
                </Select.Root>
              )}
            />
          </div>
        </div>
        <div className="grow gap-6 h-full flex flex-col">
          <div className="flex justify-between">
            <h4 className="text-h4">
              {isMobileOnly
                ? "Press and hold, then drag to add suggested time windows"
                : "Drag to add suggested time windows"}
            </h4>
          </div>
          <div className="flex gap-6 h-full overflow-hidden">
            <AvailabilityCalendar
              className="grow h-full py-1"
              selections={selections}
              overlayEvents={overlayEvents}
              setSelections={(val) => setValue("suggestions", val)}
              timezone={selectedTimezone}
              onTimezoneChange={(val) => setValue("timezone", val)}
              defaultEventTitle="Suggested time"
              initialDate={initialDate}
            />
            {enableOverlayCalendars && (
              <OverlayCalendars onChange={setOverlayEvents} />
            )}
          </div>
        </div>
      </div>
    </Dialog.View>
  );
}

export function RequestAvailabilityModal({
  isOpen,
  onClose,
  ...props
}: RequestAvailabilityModalProps) {
  const titleInputRef = useRef<HTMLInputElement>(null);
  const onModalOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose?.();
      }
    },
    [onClose]
  );

  return (
    <>
      <Dialog.Root open={isOpen} setOpen={onModalOpenChange}>
        <Dialog.Content
          size="large"
          variant="fullscreen"
          initialFocusRef={titleInputRef}
        >
          <RequestAvailabilityModalView
            initialFocusRef={titleInputRef}
            onClose={onClose}
            {...props}
          />
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
